import { Prop, Watch } from 'vue-property-decorator'
import WorkoutData from '@src/data/workout-data'
import Vue from 'vue'
import { CreateElement } from '@src/vue'
import Component from 'vue-class-component'
import { WorkoutLiveData } from '@src/state/store'
import { LiveWorkoutRetrieverAbstraction } from '@src/routes/content/live-workout-retriever-abstraction'

import { WORKOUT_SIMULTATOR_STATE, MIN_PLAYBACK_RATE } from '@src/util/constants'
import { formatDuration, getExercisePreparationSteps } from '@src/util/functions'
import { WorkoutExercise } from '@src/data/workout-exercise'
import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import { WorkoutPreview } from '@src/routes/content/workout-playground/workout-preview'
import { WorkoutExecution } from '@src/routes/content/workout-playground/workout-execution'
import { WorkoutSummary } from '@src/routes/content/workout-playground/workout-summary'

@Component({
  computed: {
    workoutLiveData () {
      return this.$store.state.workoutLiveData
    }
  }
})
export class WorkoutPlaygroundController extends Vue {

  @Prop({ required: true })
  workout: WorkoutData

  @Prop({ required: true })
  workoutSessionId: number

  currentExercise: WorkoutExercise

  workoutLiveData: WorkoutLiveData

  videoAutoplay: boolean = false

  workoutStarted: boolean = false

  timerString: string = '00:00'

  svgCircleSettings: any

  video

  exerciseStepsDescription: string[][] = []

  details: any[] = []

  updateVideoState: boolean = true

  startedAt: string = new Date().toISOString()

  private apiClient: ApiClient

  getTimerString (time) {
    if (typeof time !== 'number') return ''
    let seconds = Math.floor(time % 60)
    let minutes = Math.floor(time / 60)
    let minutesAsString = minutes < 10 ? '0' + minutes : minutes
    let secondsAsString = seconds < 10 ? '0' + seconds : seconds
    return `${minutesAsString}:${secondsAsString}`
  }

  mounted () {
    this.video = (this.$refs['childRef'] as Vue).$refs['exercise-video'] || /* istanbul ignore next: every child view has the exercise-video ref */ this.$refs['exercise-video'] as HTMLVideoElement
    /* istanbul ignore else: every child view has the exercise-video ref */
    if (this.video) this.video.addEventListener('loadedmetadata', this.onMetaDataLoad)
    window.addEventListener('resize', this.setSvgSettings)
    this.updateVideoState = false
  }

  updated () {
    if (this.updateVideoState) {
      this.video = (this.$refs['childRef'] as Vue).$refs['exercise-video'] || /* istanbul ignore next: every child view has the exercise-video ref */ this.$refs['exercise-video'] as HTMLVideoElement
      this.video.addEventListener('loadedmetadata', this.onMetaDataLoad)
      this.updateVideoState = false
    }
  }

  created () {
    this.apiClient = new ApiClient()
    this.currentExercise = this.workout.workoutExercises[this.workoutLiveData.currentExerciseIndex]
    this.setSvgSettings()
    this.setupExerciseDetails()
    this.setInitialExercisesSteps()
  }

  setInitialExercisesSteps () {
    this.exerciseStepsDescription = this.workout.workoutExercises.map((workoutExercise) => {
      return getExercisePreparationSteps(this, workoutExercise.exercise.number, 'DEFAULT', [])
    })
  }

  beforeDestroy () {
    this.video.pause()
    LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).stop()
    LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).resetData()
    window.removeEventListener('resize', this.setSvgSettings)
    this.video.removeEventListener('loadedmetadata', this.onMetaDataLoad)
  }

  homeWorkoutStartNextExercise () {
    return this.apiClient.post(API.MEMBERS_API.HOME_WORKOUT_NEXT_EXERCISE, {}, { 'workout_session_id': this.workoutSessionId })
  }

  finishHomeWorkout () {
    return this.apiClient.post(API.MEMBERS_API.HOME_WORKOUT_FINISH, {}, { 'workout_session_id': this.workoutSessionId })
  }

  async postActivity () {
    return new Promise((resolve, reject) => {
      let amount = this.workoutLiveData.totalElapsedTime
      if (this.workoutLiveData.previewTime) {
        amount = this.workoutLiveData.totalElapsedTime + (this.workoutLiveData.previewTime * this.workout.workoutExercises.length)
      }

      if (!this.startedAt || !this.workout || !amount) {
        reject('No workout or start time')
      }
      const data = {
        member_activity: {
          amount,
          status: 'completed',
          started_at: this.startedAt,
          activity_id: 39,
          meta: {
            workout_name: this.workout.name
          }
        }
      }
      this.apiClient.post(API.MEMBERS_API.MEMBER_ACTIVITIES, data).then((response) => {
        resolve(response)
      }).catch((error) => {
        reject(error)
      })
    })
  }

  onMetaDataLoad () {
    const { videoReps, durationMedium } = this.currentExercise.exercise
    if (videoReps) {
      const oneRepDuration = Math.round(this.video.duration / videoReps * 100) / 100
      const playbackRate = Number(Math.round(oneRepDuration / durationMedium * 100) / 100)
      console.log(playbackRate)
      this.video.playbackRate = playbackRate < MIN_PLAYBACK_RATE ? MIN_PLAYBACK_RATE : playbackRate
    }
    if (!this.workoutStarted) this.startWorkout()
  }

  closePage () {
    this.$router.go(-1)
  }

  startDataRetriever () {
    const { elapsedTime } = this.workoutLiveData
    if (elapsedTime === 0) LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).start(this.workout)
  }

  @Watch('workoutLiveData.currentExerciseIndex')
  onWorkoutExerciseChange () {
    this.currentExercise = this.workout.workoutExercises[this.workoutLiveData.currentExerciseIndex]
    this.setupExerciseDetails()
  }

  @Watch('workoutLiveData.state')
  updateVideoRefOnStateChange () {
    this.updateVideoState = true
  }

  @Watch('workoutLiveData', { deep: true })
  onWorkoutLiveDataUpdated () {
    const {
      state,
      elapsedTime,
      previewTime,
      totalElapsedTime,
      exerciseDone,
      workoutDone,
      repetitions
    } = this.workoutLiveData
    const totalRepetitionsPerExercise = this.currentExercise!.initialExerciseConfig!.repetitions || 0
    const progress = (repetitions / totalRepetitionsPerExercise) * 100
    const progressOffset = this.svgCircleSettings.circumference * (1.0 - (progress * 0.01))

    this.svgCircleSettings.progress = progress
    this.svgCircleSettings.progressOffset = progressOffset

    if (state === WORKOUT_SIMULTATOR_STATE.RUNNING) {
      if (elapsedTime === 0) {
        this.video.load()
        this.videoAutoplay = true
      }

      if (exerciseDone) {
        this.video.pause()
        LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).stop()
        if (workoutDone) {
          this.finishHomeWorkout().then(() => {
            LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).goToState(WORKOUT_SIMULTATOR_STATE.END)
            LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).resume()
            this.postActivity().then(
              (response) => console.log('Success post activity', response)
            ).catch((error) => console.log('Error when trying to save activity', error))

          }).catch((error) => {
            console.log('Error when trying to finish home workout', error)
            LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).goToState(WORKOUT_SIMULTATOR_STATE.END)
            LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).resume()
          })
        } else {
          this.homeWorkoutStartNextExercise().then(() => {
            LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).goToState(WORKOUT_SIMULTATOR_STATE.PREVIEW)
            LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).resume()
          }).catch((error) => {
            console.log('Error when trying to start next exercise', error)
            LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).goToState(WORKOUT_SIMULTATOR_STATE.PREVIEW)
            LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).resume()
          })
        }
      }
      this.timerString = this.getTimerString(elapsedTime)
    } if (state === WORKOUT_SIMULTATOR_STATE.PREVIEW) {
      this.timerString = this.getTimerString(previewTime)
    } if (state === WORKOUT_SIMULTATOR_STATE.END) {
      this.video.pause()
      let totalTime = totalElapsedTime
      if (previewTime) {
        totalTime = totalElapsedTime + (previewTime * this.workout.workoutExercises.length)
      }
      this.timerString = this.getTimerString(totalTime)
    }
  }

  startWorkout () {
    this.video.play()
    this.startDataRetriever()
    this.workoutStarted = true
  }

  updateWorkoutStateButton () {
    const { state, isWorkoutPaused } = this.workoutLiveData
    if (isWorkoutPaused) {
      LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).resume()
      if (state !== WORKOUT_SIMULTATOR_STATE.PREVIEW) this.video.play()
    } else {
      LiveWorkoutRetrieverAbstraction.getRetriever(this.$store).pause()
      if (state !== WORKOUT_SIMULTATOR_STATE.PREVIEW) this.video.pause()
    }
  }

  render (h: CreateElement) {
    const mp4Video = this.currentExercise.exercise.videos.find(({ content_type }) => content_type === 'video/mp4')
    const videoUrl = mp4Video ? mp4Video.url : this.currentExercise.exercise.videos[0].url
    switch (this.workoutLiveData.state) {
      case WORKOUT_SIMULTATOR_STATE.PREVIEW:
        return (
          <WorkoutPreview
            ref={'childRef'}
            workout={this.workout}
            workoutLiveData={this.workoutLiveData}
            videoAutoplay={this.videoAutoplay}
            currentExercise={this.currentExercise}
            svgCircleSettings={this.svgCircleSettings}
            exerciseStepsDescription={this.exerciseStepsDescription}
            details={this.details}
            timerString={this.timerString}
            closePage={this.closePage}
            updateWorkoutStateButton={this.updateWorkoutStateButton}
            videoUrl={videoUrl}
          />
        )
      case WORKOUT_SIMULTATOR_STATE.RUNNING:
        return (
          <WorkoutExecution
            ref={'childRef'}
            workout={this.workout}
            workoutLiveData={this.workoutLiveData}
            videoAutoplay={this.videoAutoplay}
            currentExercise={this.currentExercise}
            svgCircleSettings={this.svgCircleSettings}
            exerciseStepsDescription={this.exerciseStepsDescription}
            details={this.details}
            timerString={this.timerString}
            closePage={this.closePage}
            updateWorkoutStateButton={this.updateWorkoutStateButton}
            videoUrl={videoUrl}
          />
        )
      case WORKOUT_SIMULTATOR_STATE.END:
        return (<WorkoutSummary
          ref={'childRef'}
          workoutLiveData={this.workoutLiveData}
          videoAutoplay={this.videoAutoplay}
          currentExercise={this.currentExercise}
          svgCircleSettings={this.svgCircleSettings}
          timerString={this.timerString}
          closePage={this.closePage}
          videoUrl={videoUrl}
        />)
      default:
        return ''

    }
  }

  private setupExerciseDetails () {
    const updatedDetails: any[] = []
    let exerciseDuration = Math.round(this.currentExercise.exercise.durationMedium * (this.currentExercise!.initialExerciseConfig!.repetitions || 0)) as any
    const minuteOrMinutes = exerciseDuration >= 60 && exerciseDuration < 120 ? 'MINUTE' : 'MINUTES'
    const secondOrSeconds = exerciseDuration <= 1 ? 'SECOND' : 'SECONDS'

    if (exerciseDuration >= 60) {
      exerciseDuration = `${formatDuration(exerciseDuration).slice(3)} ${this.$t(`${minuteOrMinutes}`).toString()}`
    } else {
      exerciseDuration = `${exerciseDuration} ${this.$t(`${secondOrSeconds}`).toString()}`
    }

    if (this.currentExercise.equipmentWithWeight.length) {
      this.currentExercise.equipmentWithWeight.forEach(({ name, weight, weightUnit }, index) => {
        const equipmentItem = {
          icon: `equipment/${name.toLowerCase()}`,
          label: weight && weightUnit ? `${weight} ${weightUnit}` : ''
        }
        updatedDetails.push(equipmentItem)
      })
    } else {
      updatedDetails.push({
        icon: 'equipment/no-equipment',
        label: `${this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.NO_EQUIPMENT').toString()}`
      })
    }

    updatedDetails.push({
      icon: 'workout/time',
      label: exerciseDuration
    })

    updatedDetails.push({
      icon: 'workout/repetitions',
      label: `${this.currentExercise!.initialExerciseConfig!.repetitions} ${this.$t('PROMPTS.REPS')}`
    })

    this.details = [...updatedDetails]
  }

  private setSvgSettings () {
    const radius = (20 * (Math.min(window.innerHeight, window.innerWidth) / 100)) / 2
    const outerRadius = 2 * radius
    const innerRadius = radius - 6 * 0.5
    const circumference = 2 * Math.PI * innerRadius

    this.svgCircleSettings = {
      radius,
      progress: 0,
      progressOffset: 0,
      outerRadius,
      innerRadius,
      circumference
    }
  }

}
